import { MutationCache, QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
  // Any successful mutation may have written an audit-log row, so refresh any
  // mounted change-history view. Done once here rather than per-hook so every
  // page's history stays current — including entities added later. Cheap:
  // audit queries only mount on a detail page's History tab.
  // The dashboard-insights and org-health aggregates get the same treatment:
  // they summarize people/roles/certifications, so almost any mutation can move
  // them (e.g. adding a hire must surface in "New to Nymbl" without waiting out
  // staleTime), they're read-only with no per-hook wiring, and their queries
  // only mount on the dashboard / Org Health pages.
  // `options` is here for a different reason: every EntityCombobox in the app
  // reads /api/options/<entity>, and twelve of those sources ARE mutable rows
  // (people, roles, skills, pillars, departments, role teams, the config
  // lists…). Nothing invalidated this prefix, so renaming a pillar left every
  // picker showing the old name until staleTime expired. Per-hook wiring would
  // mean touching a dozen hooks and re-deciding it for the thirteenth; only
  // mounted queries refetch, and eleven of the twelve sources are a single
  // indexed `select {id, label}`.
  // (Keys are literals — `auditKeys.all` etc. would forward-reference this
  // initializer.)
  mutationCache: new MutationCache({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["org-health"] });
      queryClient.invalidateQueries({ queryKey: ["options"] });
    },
  }),
});

// ── Query key factories ──

/**
 * Where a person stands against their role's required capabilities — EPIC-023.
 * Its own factory rather than a `personKeys` branch: the standalone report
 * reads the same data for a person the viewer did not navigate to, and
 * invalidating "this person's profile" should not drop it.
 */
export const capabilityGapKeys = {
  all: ["capability-gaps"] as const,
  forPerson: (personId: string) => ["capability-gaps", "person", personId] as const,
  /** `null` means "the viewer's own direct reports". */
  forTeam: (roleId: string | null) => ["capability-gaps", "team", roleId] as const,
};

export const personKeys = {
  all: ["persons"] as const,
  list: (filters: {
    search?: string;
    statuses?: string[];
    employmentTypes?: string[];
    roleClassifications?: string[];
  }) => ["persons", "list", filters] as const,
  detail: (id: string) => ["persons", "detail", id] as const,
  pending: (id: string) => ["persons", "detail", id, "pending"] as const,
  history: (id: string, attribute: string) =>
    ["persons", "detail", id, "history", attribute] as const,
  directReports: (id: string) => ["persons", "detail", id, "direct-reports"] as const,
  dottedLineReports: (id: string) => ["persons", "detail", id, "dotted-line-reports"] as const,
  aspirations: (id: string) => ["persons", "detail", id, "aspirations"] as const,
  // Project assignments + guidance, read live from the gateway rather than our
  // own database — so this cache is the only copy and nothing invalidates it.
  projects: (id: string) => ["persons", "detail", id, "projects"] as const,
  // This person's direct reports with NOS guidance + logged time (Team page).
  // Also gateway-backed, so the same "only copy" caveat applies.
  teamSummary: (id: string, scope: string = "team") =>
    ["persons", "detail", id, "team-summary", scope] as const,
  aspirationReport: ["persons", "aspiration-report"] as const,
  // The signed-in user's own person record (self-service surfaces: dashboard
  // hero, My Profile, My Certifications). Its own key so a self-edit refreshes
  // those without touching the admin directory caches.
  mine: ["my-person"] as const,
};

// Role-aware landing-page insights (dashboard home). Read-only — kept fresh by
// the global MutationCache invalidation above (any mutation can move them).
export const dashboardKeys = {
  all: ["dashboard"] as const,
  insights: ["dashboard", "insights"] as const,
  notStartedCerts: ["dashboard", "not-started-certs"] as const,
} as const;

export const orgHealthKeys = {
  all: ["org-health"] as const,
  overview: (filters: string) => ["org-health", "overview", filters] as const,
  headcount: (filters: string) => ["org-health", "headcount", filters] as const,
} as const;

export const announcementKeys = {
  all: ["announcements"] as const,
  active: ["announcements", "active"] as const,
  admin: (filters?: { search?: string; filter?: string }) =>
    ["announcements", "admin", filters] as const,
  prior: ["announcements", "prior"] as const,
} as const;

export const userKeys = {
  all: ["users"] as const,
  list: (filters?: { search?: string; roles?: string[] }) => ["users", "list", filters] as const,
} as const;

export const devKeys = {
  seedAccounts: ["dev", "seed-accounts"] as const,
} as const;

/** Which ways of signing in this environment offers (read before any session). */
export const authMethodKeys = {
  all: ["auth-methods"] as const,
} as const;

/** Workspace feature availability (Settings → Config → Feature availability). */
export const appSettingsKeys = {
  all: ["app-settings"] as const,
} as const;

/**
 * Review form templates. `detail` is keyed per template because the builder
 * edits three child collections against one id — a single `all` key would make
 * every save refetch every form.
 */
export const reviewTemplateKeys = {
  all: ["review-templates"] as const,
  list: (params: Record<string, unknown> = {}) => ["review-templates", "list", params] as const,
  detail: (id: string) => ["review-templates", "detail", id] as const,
  relationshipTypes: ["review-relationship-types"] as const,
} as const;

/** One person's review, read as a whole. */
export const reviewReportKeys = {
  all: ["review-reports"] as const,
  detail: (id: string) => ["review-reports", id] as const,
  /** Every review a person has ever had, in one place — EPIC-070. */
  personHistory: (personId: string) => ["review-reports", "person-history", personId] as const,
} as const;

/** What one person has been asked to fill in, and the form for one of them. */
export const reviewTaskKeys = {
  all: ["review-tasks"] as const,
  mine: ["review-tasks", "mine"] as const,
  /** A person's own results from closed rounds — not work to do. */
  myResults: ["review-tasks", "my-results"] as const,
  /** Closed-round reviews of the people you manage — sign-off lives here. */
  teamResults: ["review-tasks", "team-results"] as const,
  detail: (id: string) => ["review-tasks", "detail", id] as const,
} as const;

/** The peer-derivation exclusion list — EPIC-056 WI-201. */
export const peerExclusionKeys = {
  all: ["review-peer-exclusions"] as const,
  search: (term: string) => ["review-peer-exclusions", "search", term] as const,
} as const;

/** Self-service reviewer requests — EPIC-058. */
export const reviewerRequestKeys = {
  mine: ["reviewer-requests", "mine"] as const,
  openSubjects: (search: string) => ["reviewer-requests", "open-subjects", search] as const,
  queue: (cycleId: string) => ["reviewer-requests", "queue", cycleId] as const,
} as const;

/** A reviewer's own endorsements, and how many they have left. */
export const reviewEndorsementKeys = {
  all: ["review-endorsements"] as const,
  mine: (reviewerId: string) => ["review-endorsements", reviewerId] as const,
} as const;

/** The catalogue of questions a review form can ask. */
export const reviewCriteriaKeys = {
  all: ["review-criteria"] as const,
  list: (activeOnly: boolean) => ["review-criteria", { activeOnly }] as const,
} as const;

/** Written feedback: a reviewer's own notes, and the tags they can be given. */
export const reviewFeedbackKeys = {
  all: ["review-feedback"] as const,
  mine: (reviewerId: string) => ["review-feedback", "mine", reviewerId] as const,
  sentiments: ["review-feedback", "sentiments"] as const,
} as const;

/** Standalone "give feedback anytime" — EPIC-067. Also backs post-project feedback (EPIC-068). */
export const adHocFeedbackKeys = {
  forPerson: (personId: string) => ["ad-hoc-feedback", "person", personId] as const,
  projectSearch: (term: string) => ["ad-hoc-feedback", "project-search", term] as const,
  projectPrompts: ["ad-hoc-feedback", "project-prompts"] as const,
} as const;

/** Review rounds, their generation preview, and the reviews beneath them. */
export const reviewCycleKeys = {
  all: ["review-cycles"] as const,
  detail: (id: string) => ["review-cycles", "detail", id] as const,
  preview: (id: string) => ["review-cycles", "preview", id] as const,
  stragglers: (id: string) => ["review-cycles", "stragglers", id] as const,
  signOffStatus: (id: string) => ["review-cycles", "sign-off-status", id] as const,
  notificationLog: (id: string, search: string) =>
    ["review-cycles", "notification-log", id, search] as const,
  excludedReviewers: (id: string) => ["review-cycles", "excluded-reviewers", id] as const,
  confidentialAnswers: (id: string) => ["review-cycles", "confidential-answers", id] as const,
  /** EPIC-059: each person's assigned-review count for the round (roster screen). */
  reviewerWorkload: (id: string) => ["review-cycles", "reviewer-workload", id] as const,
  /** EPIC-073 WI-277: per-reviewer progress (total vs completed, by relationship). */
  workload: (id: string) => ["review-cycles", "workload", id] as const,
} as const;

/** Recognition across one or more review rounds — EPIC-074. `[]` is "All time". */
export const reviewRecognitionKeys = {
  all: ["review-recognition"] as const,
  endorsements: (cycleIds: string[]) => ["review-recognition", "endorsements", cycleIds] as const,
  rankings: (cycleIds: string[]) => ["review-recognition", "rankings", cycleIds] as const,
  topPerformers: (cycleIds: string[]) =>
    ["review-recognition", "top-performers", cycleIds] as const,
} as const;

/** HR's cycle-wide feedback triage — EPIC-072. */
export const cycleFeedbackKeys = {
  all: ["cycle-feedback"] as const,
  list: (cycleId: string, filters: Record<string, string | undefined>) =>
    ["cycle-feedback", "list", cycleId, filters] as const,
  themes: (cycleId: string) => ["cycle-feedback", "themes", cycleId] as const,
} as const;

/** The four review reference lists at Settings → Config → Reviews. */
export const reviewConfigKeys = {
  all: ["review-config"] as const,
  list: (list: string) => ["review-config", list] as const,
} as const;

export const themeKeys = {
  all: ["theme"] as const,
} as const;

export const auditKeys = {
  all: ["audit"] as const,
  entity: (entityType: string, entityId: string) => ["audit", entityType, entityId] as const,
  global: (filters: {
    entityTypes: string[];
    actions: string[];
    actorUserId: string;
    from: string;
    to: string;
  }) => ["audit", "global", filters] as const,
  entityTypes: ["audit", "entity-types"] as const,
} as const;

export const organizationKeys = {
  all: ["organization"] as const,
  tree: () => ["organization", "tree"] as const,
  chart: () => ["organization", "chart"] as const,
} as const;

/** Categories — the merged skill-category/competency-theme grouping. */
export const categoryKeys = {
  all: ["categories"] as const,
  skillMembers: (categoryId: string) => ["categories", categoryId, "skills"] as const,
  competencyMembers: (categoryId: string) => ["categories", categoryId, "competencies"] as const,
} as const;

export const changeReasonKeys = {
  all: ["change-reasons"] as const,
} as const;

export const companyTenetKeys = {
  all: ["company-tenets"] as const,
  active: ["company-tenets", "active"] as const,
} as const;

export const employmentTypeKeys = {
  all: ["employment-types"] as const,
} as const;

export const employeeProfileKeys = {
  all: ["employee-profiles"] as const,
} as const;

export const roleClassificationKeys = {
  all: ["role-classifications"] as const,
} as const;

export const workLocationKeys = {
  all: ["work-locations"] as const,
} as const;

/**
 * Staffing constraints (EPIC-043). Separate lists rather than one key: the
 * surfaces are independent, and retyping a rung's definition shouldn't refetch
 * the value list — which now costs a round trip to NOS.
 */
export const constraintTypeKeys = {
  all: ["constraint-types"] as const,
} as const;

/** The NOS-backed pick list, plus Enable's hide overlay. */
export const constraintValueKeys = {
  all: ["constraint-values"] as const,
} as const;

export const constraintLevelKeys = {
  all: ["constraint-levels"] as const,
} as const;

/** One person's constraint ratings, keyed per person like their other sections. */
export const personConstraintKeys = {
  all: ["person-constraints"] as const,
  list: (personId: string) => ["person-constraints", personId] as const,
} as const;

/** The constraints directory: the constraint list, and one constraint's holders. */
export const constraintDirectoryKeys = {
  // `includeInactive` is part of the key on BOTH: it changes the counts the
  // master list shows and the rows the detail panel shows, so a shared key
  // would let one half render another filter's data.
  all: (includeInactive: boolean) => ["constraint-directory", { includeInactive }] as const,
  holders: (typeKey: string, valueId: string, levels: number[], includeInactive: boolean) =>
    [
      "constraint-directory",
      typeKey,
      valueId,
      "holders",
      [...levels].sort(),
      { includeInactive },
    ] as const,
} as const;

export const hrNotificationKeys = {
  all: ["hr-notification-emails"] as const,
  list: (purpose: string) => ["hr-notification-emails", purpose] as const,
} as const;

export const emailOutboxKeys = {
  all: ["email-outbox"] as const,
  detail: (id: string) => ["email-outbox", "detail", id] as const,
} as const;

/** Editable email templates (Settings → Config → Notification Templates). */
export const emailTemplateKeys = {
  all: ["email-templates"] as const,
  missed: (key: string) => ["email-templates", "missed", key] as const,
  preview: (key: string, content: string) => ["email-templates", "preview", key, content] as const,
} as const;

export const skillKeys = {
  all: ["skills"] as const,
  list: (filters: { search?: string; statuses?: string[]; categoryIds?: string[] }) =>
    ["skills", "list", filters] as const,
  detail: (id: string) => ["skills", "detail", id] as const,
  people: (id: string) => ["skills", "detail", id, "people"] as const,
} as const;

// Five-level breakdowns (shared by skills and competencies - WI-027/WI-059).
export const capabilityLevelKeys = {
  all: ["capability-levels"] as const,
  scale: ["capability-levels", "scale"] as const,
} as const;

// The career-level catalogue (WI-102) — Associate/Staff/Senior/Principal and
// the optional Department / Pillar Head. Distinct from `capabilityLevelKeys`
// above, which is the five-point performance-rating scale.
export const roleLevelKeys = {
  all: ["role-levels"] as const,
  catalog: ["role-levels", "catalog"] as const,
  /** Catalogue annotated for one role (applies + peopleCount) — WI-104. */
  forRole: (roleId: string) => ["role-levels", "for-role", roleId] as const,
  /** One role's expectation grid at one level — WI-105 / WI-107. */
  expectations: (roleId: string, roleLevelId: string) =>
    ["role-levels", "expectations", roleId, roleLevelId] as const,
  /**
   * Every level's grid for one role. Prefix of `expectations`, so invalidating
   * it refreshes each level the admin has looked at — which levels are cached
   * isn't knowable from a mutation. Changing the role's required skills or
   * competencies changes the grid's rows, and this key is what carries that.
   */
  expectationsForRole: (roleId: string) => ["role-levels", "expectations", roleId] as const,
} as const;

export const competencyKeys = {
  all: ["competencies"] as const,
  list: (filters: { search?: string; statuses?: string[]; categoryIds?: string[] }) =>
    ["competencies", "list", filters] as const,
  detail: (id: string) => ["competencies", "detail", id] as const,
  people: (id: string) => ["competencies", "detail", id, "people"] as const,
} as const;

export const certificationKeys = {
  all: ["certifications"] as const,
  list: (filters: { search?: string; statuses?: string[] }) =>
    ["certifications", "list", filters] as const,
  detail: (id: string) => ["certifications", "detail", id] as const,
  people: (id: string) => ["certifications", "detail", id, "people"] as const,
} as const;

// Server-searchable entity option pickers (EntityCombobox). One factory for
// every entity exposed by `/api/options/:entity`.
export const optionKeys = {
  all: ["options"] as const,
  list: (
    entity: string,
    filters: {
      search?: string;
      filters?: Record<string, string | undefined>;
      /** Sorted, comma-joined exclusion list — part of the cache identity. */
      excludeIds?: string;
    },
  ) => ["options", entity, "list", filters] as const,
  detail: (entity: string, ids: string) => ["options", entity, "detail", ids] as const,
} as const;

// Role-to-role career-path comparison (EPIC-029). Read-only — no
// invalidation wiring needed.
export const careerPathKeys = {
  all: ["career-path"] as const,
  compare: (fromId: string, toId: string) => ["career-path", "compare", fromId, toId] as const,
} as const;

/** The one company-wide management capability set (WI-311). */
export const managementCompetencyKeys = {
  all: ["management-competencies"] as const,
} as const;

export const roleKeys = {
  all: ["roles"] as const,
  list: (filters: {
    search?: string;
    statuses?: string[];
    departmentId?: string;
    roleTeamId?: string;
  }) => ["roles", "list", filters] as const,
  detail: (id: string) => ["roles", "detail", id] as const,
  holders: (id: string) => ["roles", "detail", id, "holders"] as const,
  // Read-only role × capability requirement grid. Refetched on mount; kept
  // fresh by the global audit-refresh only.
  matrix: ["roles", "matrix"] as const,
} as const;

// Automatically captured frontend errors (Settings → Error log). `all` is what
// a status change invalidates — the row moves between the open and fixed tabs,
// so both lists are stale.
export const errorLogKeys = {
  all: ["error-log"] as const,
  list: (filters: { view: string; search: string }) => ["error-log", "list", filters] as const,
} as const;

// Outbound person pushes to NOS 1.0 (Settings → NOS sync). `all` is what a
// retry invalidates — a retried row moves from `open` back into `open`
// (pending), but its `attempts`/`lastError` change, so the whole tree refetches
// rather than guessing which view is stale.
export const nosPushKeys = {
  all: ["nos-push"] as const,
  list: (filters: { view: string }) => ["nos-push", "list", filters] as const,
  historyList: (filters: { view: string }) => ["nos-push", "history", filters] as const,
} as const;

export const releaseKeys = {
  all: ["releases"] as const,
  admin: ["releases", "admin"] as const,
  public: ["releases", "public"] as const,
} as const;

export const appVersionKeys = {
  all: ["app-version"] as const,
} as const;

export const ptoKeys = {
  all: ["pto"] as const,
  range: (from: string, to: string) => ["pto", from, to] as const,
  /** One person's upcoming leave, for their profile's Time off tab. */
  person: (personId: string) => ["pto", "person", personId] as const,
} as const;

/**
 * The People Targets report. Read-only, so nothing invalidates these — the
 * person branch hangs off `all` only so a manual refresh can sweep both.
 */
export const peopleTargetKeys = {
  all: ["people-targets"] as const,
  list: () => ["people-targets", "list"] as const,
  /** One person's monthly targets, loaded when they're selected. */
  periods: (personId: string) => ["people-targets", "person", personId] as const,
} as const;
